import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Utensils, Loader2 } from 'lucide-react';
import PageLayout from './PageLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public Wedding Menu.
 *
 * Reads the couple's live catering plan from `/api/menu/content` (which
 * server-side strips any section, category, or item flagged hidden), and
 * lays it out in the same ivory/champagne visual language the rest of
 * the site uses — never a restaurant list, never an admin table.
 *
 * Wrapped in the shared `PageLayout` so it inherits the compact
 * minimal footer used across every other public sub-page (Events,
 * Schedule, Travel, Guest Book, FAQ). Prevents extra scroll on this
 * already-long page.
 */
const MenuPage = () => {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/menu/content`, { params: { _: Date.now() } });
        setMenu(r.data);
      } catch {
        setError('We could not load the menu just now.');
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <PageLayout testId="menu-page">
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-8 pb-24">
        <motion.header
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-white shadow-sm flex items-center justify-center text-[#C8A951] mb-6">
            <Utensils className="w-6 h-6" />
          </div>
          <p className="text-xs uppercase tracking-[0.32em] text-[#C8A951] mb-3">A Taste of the Celebration</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#3B2F2F] tracking-tight">Wedding Menu</h1>
          <p className="mt-4 text-[#5A4A4A] max-w-xl mx-auto">
            {(menu?.tagline || 'A thoughtfully prepared selection for our celebration.')}
          </p>
          <div className="mt-8 mx-auto w-24 h-px bg-[#C8A951]" />
        </motion.header>

        {loading && (
          <div className="mt-16 flex justify-center text-[#8A7A7A] gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading menu…
          </div>
        )}

        {!loading && (error || !menu || !menu.sections?.length) && (
          <p className="mt-16 text-center text-[#8A7A7A] italic" data-testid="menu-empty">
            {error || 'The menu is being finalized — check back soon.'}
          </p>
        )}

        {!loading && menu?.visible === false && (
          <p className="mt-16 text-center text-[#8A7A7A] italic" data-testid="menu-hidden">
            The wedding menu will be shared closer to the day.
          </p>
        )}

        {!loading && menu?.visible !== false && (() => {
          // Split the couple's sections into food vs drinks so we can
          // slot the dietary-restrictions note between them — the note
          // belongs to the buffet, not the open bar. Any section whose
          // name or style mentions drinks / beverages / bar / cocktail
          // is treated as bar service, which keeps the split working
          // even if the couple renames the section to "Open Bar &
          // Beverages" later.
          const isDrinks = (s) => {
            const n = (s?.name || '').toLowerCase();
            const st = (s?.style || '').toLowerCase();
            return /drink|beverage|bar|cocktail/.test(n) || /bar/.test(st);
          };
          const sections = menu?.sections || [];
          const foodSections = sections.filter((s) => !isDrinks(s));
          const drinkSections = sections.filter(isDrinks);

          const renderSection = (section, si, key) => (
            <motion.section
              key={key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: si * 0.05 }}
              className="mt-16"
              data-testid={`menu-section-${si}`}
            >
              <div className="text-center mb-8">
                <h2 className="font-serif text-3xl sm:text-4xl text-[#3B2F2F]">{section.name}</h2>
                {section.style && (
                  <p className="mt-2 text-xs uppercase tracking-[0.3em] text-[#8A7A6A]">{section.style}</p>
                )}
              </div>

              <div className="space-y-8">
                {(section.categories || []).map((cat, ci) => (
                  <div key={cat.id || ci} className="wedding-card p-6 sm:p-8" data-testid={`menu-category-${si}-${ci}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="w-8 h-px bg-[#C8A951]" />
                      <h3 className="font-serif text-xl text-[#3B2F2F]">{cat.name}</h3>
                      <span className="flex-1 h-px bg-[#E5E0D8]" />
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                      {(cat.items || []).map((item, ii) => (
                        <li
                          key={item.id || ii}
                          className="flex items-baseline gap-3 py-1.5 text-[#3B2F2F]"
                          data-testid={`menu-item-${si}-${ci}-${ii}`}
                        >
                          <span className="text-[#C8A951] text-xs mt-1">◆</span>
                          <span className="font-serif text-lg leading-snug">{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.section>
          );

          return (
            <>
              {foodSections.map((section) => {
                const si = sections.indexOf(section);
                return renderSection(section, si, section.id || `food-${si}`);
              })}

              {/* Dietary / allergy note — anchored to the buffet menu.
                  Rendered only when there is at least one food section
                  so a drinks-only menu never carries a stray note. */}
              {foodSections.length > 0 && (
                <div
                  className="mt-12 text-center text-sm sm:text-base text-[#5A4A4A] italic max-w-2xl mx-auto px-4"
                  data-testid="menu-dietary-note"
                >
                  Kindly note any dietary restrictions or food allergies on your RSVP so we can care for you at the buffet.
                </div>
              )}

              {/* Section divider — only appears when both a food menu
                  and a bar section exist. Extra top spacing + hairline
                  ornament makes the "food ends here, drinks begin" cue
                  unmistakable without adding another headline. */}
              {foodSections.length > 0 && drinkSections.length > 0 && (
                <div
                  className="mt-20 flex items-center justify-center gap-4"
                  aria-hidden="true"
                  data-testid="menu-food-drinks-divider"
                >
                  <span className="w-16 h-px bg-[#C8A951]/60" />
                  <span className="text-[#C8A951] text-lg">◆</span>
                  <span className="w-16 h-px bg-[#C8A951]/60" />
                </div>
              )}

              {drinkSections.map((section) => {
                const si = sections.indexOf(section);
                return renderSection(section, si, section.id || `drink-${si}`);
              })}
            </>
          );
        })()}
      </section>
    </PageLayout>
  );
};

export default MenuPage;
